import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  getMOMFieldConfigurationsGrouped,
  MOMFieldConfiguration,
  MOMFieldConfigurationGrouped,
} from '../services/api/momService';

/**
 * MOM Form Configuration Context
 * Provides global access to MOM field configuration data
 */

export type FieldScope = 'mom' | 'action_item' | 'attendee';

interface MOMFormConfigContextType {
  fieldConfigs: MOMFieldConfigurationGrouped;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  isFieldEnabled: (scope: FieldScope, fieldName: string) => boolean;
  isFieldRequired: (scope: FieldScope, fieldName: string) => boolean;
  getFieldDisplayName: (scope: FieldScope, fieldName: string, language?: 'en' | 'ar') => string;
  getFieldConfig: (scope: FieldScope, fieldName: string) => MOMFieldConfiguration | undefined;
}

const MOMFormConfigContext = createContext<MOMFormConfigContextType | undefined>(undefined);

interface MOMFormConfigProviderProps {
  children: ReactNode;
}

const defaultConfigs: MOMFieldConfigurationGrouped = {
  mom: [],
  action_item: [],
  attendee: [],
};

export const MOMFormConfigProvider: React.FC<MOMFormConfigProviderProps> = ({ children }) => {
  const [fieldConfigs, setFieldConfigs] = useState<MOMFieldConfigurationGrouped>(defaultConfigs);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError(null);

      const configs = await getMOMFieldConfigurationsGrouped();
      setFieldConfigs(configs);
    } catch (err: any) {
      console.error('Error fetching MOM form configuration:', err);
      setError(err.message || 'Failed to load MOM form configuration');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh configuration - invalidates cache and re-fetches all data
   * Call this after updating field configurations
   */
  const refreshConfig = async () => {
    console.log('Refreshing MOM form configuration...');
    await fetchConfigs();
    console.log('MOM form configuration refreshed');
  };

  /**
   * Check if a field is enabled
   */
  const isFieldEnabled = (scope: FieldScope, fieldName: string): boolean => {
    const scopeConfigs = fieldConfigs[scope];
    if (!scopeConfigs || scopeConfigs.length === 0) {
      // If no configs loaded, default to enabled
      return true;
    }
    const config = scopeConfigs.find(c => c.field_name === fieldName);
    return config ? config.is_enabled : true;
  };

  /**
   * Check if a field is required
   */
  const isFieldRequired = (scope: FieldScope, fieldName: string): boolean => {
    const scopeConfigs = fieldConfigs[scope];
    if (!scopeConfigs || scopeConfigs.length === 0) {
      return false;
    }
    const config = scopeConfigs.find(c => c.field_name === fieldName);
    return config ? (config.is_enabled && config.is_required) : false;
  };

  /**
   * Get display name for a field
   */
  const getFieldDisplayName = (scope: FieldScope, fieldName: string, language: 'en' | 'ar' = 'en'): string => {
    const scopeConfigs = fieldConfigs[scope];
    if (!scopeConfigs || scopeConfigs.length === 0) {
      return fieldName;
    }
    const config = scopeConfigs.find(c => c.field_name === fieldName);
    if (!config) return fieldName;

    if (language === 'ar' && config.display_name_ar) {
      return config.display_name_ar;
    }
    return config.display_name || fieldName;
  };

  /**
   * Get full field configuration
   */
  const getFieldConfig = (scope: FieldScope, fieldName: string): MOMFieldConfiguration | undefined => {
    const scopeConfigs = fieldConfigs[scope];
    if (!scopeConfigs || scopeConfigs.length === 0) {
      return undefined;
    }
    return scopeConfigs.find(c => c.field_name === fieldName);
  };

  // Initial load on mount - only if user has a token (is authenticated)
  useEffect(() => {
    // Check if user is authenticated by checking for access token
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchConfigs();
    } else {
      // If not authenticated, set loading to false to avoid loading state
      setLoading(false);
    }

    // Listen for custom login event to fetch configs after successful login
    const handleLoginEvent = () => {
      fetchConfigs();
    };

    window.addEventListener('user-logged-in', handleLoginEvent);
    return () => window.removeEventListener('user-logged-in', handleLoginEvent);
  }, []);

  const value: MOMFormConfigContextType = {
    fieldConfigs,
    loading,
    error,
    refreshConfig,
    isFieldEnabled,
    isFieldRequired,
    getFieldDisplayName,
    getFieldConfig,
  };

  return (
    <MOMFormConfigContext.Provider value={value}>
      {children}
    </MOMFormConfigContext.Provider>
  );
};

/**
 * Custom hook to access MOM form configuration context
 * @throws Error if used outside of MOMFormConfigProvider
 */
export const useMOMFormConfigContext = () => {
  const context = useContext(MOMFormConfigContext);
  if (context === undefined) {
    throw new Error('useMOMFormConfigContext must be used within a MOMFormConfigProvider');
  }
  return context;
};

/**
 * Custom hook that provides a safe way to access MOM form config
 * Returns default values if context is not available
 */
export const useMOMFormConfig = () => {
  const context = useContext(MOMFormConfigContext);

  // Default implementations if context is not available
  const defaults = {
    fieldConfigs: defaultConfigs,
    loading: false,
    error: null,
    refreshConfig: async () => {},
    isFieldEnabled: (_scope: FieldScope, _fieldName: string) => true,
    isFieldRequired: (_scope: FieldScope, _fieldName: string) => false,
    getFieldDisplayName: (_scope: FieldScope, fieldName: string) => fieldName,
    getFieldConfig: (_scope: FieldScope, _fieldName: string) => undefined as MOMFieldConfiguration | undefined,
  };

  return context || defaults;
};

export default MOMFormConfigContext;
